import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { MessageSquare, User as UserIcon, Bot } from "lucide-react";

export default function Conversations() {
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadList = () => {
      api.get("/conversations").then((r) => {
        setList(r.data);
        setSelected((prev) => prev || (r.data.length > 0 ? r.data[0] : null));
      });
    };
    loadList();
    const id = setInterval(loadList, 15000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    const loadMsgs = () => {
      api.get(`/users/${selected.telegram_id}/messages?limit=200`).then((r) => {
        setMessages(r.data);
        setLoading(false);
      });
    };
    loadMsgs();
    const id = setInterval(loadMsgs, 15000);
    return () => clearInterval(id);
  }, [selected]);

  return (
    <div className="h-[calc(100vh-8rem)] flex gap-6 max-w-[1400px]" data-testid="conversations-page">
      {/* List */}
      <aside className="w-80 flex-shrink-0 border border-zinc-800 rounded-sm bg-zinc-900/50 flex flex-col">
        <div className="p-4 border-b border-zinc-800">
          <div className="overline">Conversaciones</div>
          <h2 className="font-display font-bold text-lg mt-1">{list.length} usuarios</h2>
        </div>
        <div className="flex-1 overflow-y-auto">
          {list.length === 0 && (
            <div className="p-6 text-sm text-zinc-500">
              Sin conversaciones aún.
            </div>
          )}
          {list.map((c) => (
            <button
              key={c.telegram_id}
              onClick={() => setSelected(c)}
              data-testid={`conversation-item-${c.telegram_id}`}
              className={`w-full text-left p-4 border-b border-zinc-900 hover:bg-zinc-900/70 transition-colors ${
                selected?.telegram_id === c.telegram_id ? "bg-zinc-900 border-l-2 border-l-[#2AABEE]" : ""
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="font-medium text-sm truncate">
                  {c.first_name || "Usuario"} {c.username ? `@${c.username}` : ""}
                </div>
                <span className="text-[10px] text-zinc-600 font-mono">{c.count}</span>
              </div>
              <div className="text-xs text-zinc-500 truncate">{c.last_message}</div>
              <div className="text-[10px] text-zinc-600 font-mono mt-1">
                {new Date(c.last_at).toLocaleString("es-MX")}
              </div>
            </button>
          ))}
        </div>
      </aside>

      {/* Thread */}
      <div className="flex-1 border border-zinc-800 rounded-sm bg-zinc-900/50 flex flex-col min-w-0">
        {!selected ? (
          <div className="flex-1 flex items-center justify-center text-zinc-500">
            <div className="text-center">
              <MessageSquare size={32} className="mx-auto mb-3 text-zinc-700" />
              <div className="text-sm">Elige una conversación a la izquierda</div>
            </div>
          </div>
        ) : (
          <>
            <div className="p-4 border-b border-zinc-800 flex items-center gap-3">
              <div className="w-9 h-9 rounded-sm bg-zinc-800 flex items-center justify-center text-sm font-bold text-[#2AABEE]">
                {(selected.first_name || "U")[0].toUpperCase()}
              </div>
              <div>
                <div className="font-medium">
                  {selected.first_name || "Usuario"}
                  {selected.username ? ` · @${selected.username}` : ""}
                </div>
                <div className="text-xs text-zinc-500 font-mono">ID {selected.telegram_id}</div>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="conversation-thread">
              {loading ? (
                <div className="text-sm text-zinc-500">Cargando…</div>
              ) : messages.length === 0 ? (
                <div className="text-sm text-zinc-500">Sin mensajes.</div>
              ) : (
                messages.map((m) => {
                  const isUser = m.role === "user";
                  return (
                    <div key={m.id} className={`flex gap-3 ${isUser ? "" : "flex-row-reverse"}`}>
                      <div
                        className={`w-7 h-7 rounded-sm flex items-center justify-center flex-shrink-0 ${
                          isUser ? "bg-zinc-800 text-zinc-400" : "bg-[#2AABEE]/10 text-[#2AABEE]"
                        }`}
                      >
                        {isUser ? <UserIcon size={14} /> : <Bot size={14} />}
                      </div>
                      <div className={`max-w-[75%] ${isUser ? "" : "text-right"}`}>
                        <div
                          className={`text-xs text-zinc-600 mb-1 ${isUser ? "" : "text-right"} font-mono`}
                        >
                          {isUser ? "Usuario" : "Bot"} ·{" "}
                          {new Date(m.created_at).toLocaleString("es-MX")} ·{" "}
                          <span className="uppercase">{m.kind}</span>
                        </div>
                        <div
                          className={`inline-block px-3.5 py-2.5 rounded-sm border text-sm whitespace-pre-wrap leading-relaxed ${
                            isUser
                              ? "bg-zinc-900 border-zinc-800 text-zinc-200"
                              : "bg-[#2AABEE]/5 border-[#2AABEE]/20 text-zinc-100"
                          }`}
                        >
                          {m.content}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
